import { FormInput } from '@systemeio/ui';
import { ActivateWebsiteModal } from 'modules/websites/components/activate-website-modal';
import { FormSelectUserDomainWithWebsite } from 'modules/websites/components/form-select-user-domain-with-website';
import { websiteTableColumns } from 'modules/websites/configs/website-table-columns';
import { websiteDefaultCreateValues, websiteDefaultErrors } from 'modules/websites/constants/website-default-values';
import { WebsiteLoco } from 'modules/websites/constants/website-loco';
import useUserDomainsWithWebsite from 'modules/websites/hooks/use-user-domains-with-website';
import { useWebsiteActions } from 'modules/websites/hooks/use-website-actions';
import { useWebsites } from 'modules/websites/hooks/use-websites';
import { WebsiteCreateInterface, WebsiteId, WebsiteInterface, WebsiteUpdateInterface } from 'modules/websites/types/website-interface';
import { NextPage } from 'next';
import React, { useEffect, useMemo, useState } from 'react';
import Badge from 'shared/components/badge';
import Confirmation from 'shared/components/confirmation-modal';
import FormUrlPathInput from 'shared/components/form-url-path-input';
import ListingLayout from 'shared/components/listing-layout';
import { Table } from 'shared/components/table/table';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { handle422Error } from 'shared/utils/parse-422-errors';
import { generatePathFromName } from 'shared/utils/path-utils';
import { twMerge } from 'tailwind-merge';
import { WebsiteTemplateModal } from 'modules/websites/components/wepsite-template-modal';
import WebsiteCreateModal from 'modules/websites/components/website-create-modal';
import { useModal } from 'shared/hooks/use-modal';
import { getUrlWebsiteView } from 'modules/websites/utils/get-url';
const Website: NextPage = () => {
  const {
    t,
    tICU
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const {
    websites,
    mutate,
    revalidateWebsiteModule
  } = useWebsites();
  const {
    updateStatus,
    removeWebsite,
    createWebsite,
    updateWebsite
  } = useWebsiteActions();
  const {
    userDomainsWithWebsite,
    revalidateUserDomainsWithWebsite,
    isDomainOccupied
  } = useUserDomainsWithWebsite();
  const [tempState, setTempState] = useState<WebsiteCreateInterface>(websiteDefaultCreateValues);
  const [errors, setErrors] = useState(websiteDefaultErrors);
  const templateModal = useModal<number>(false, true, false);
  const onCreate = async (website: WebsiteCreateInterface) => {
    try {
      setErrors(websiteDefaultErrors);
      const responseData = await createWebsite(website);
      await mutate(data => data && {
        ...data,
        total: data.total + 1,
        items: [responseData, ...data.items]
      }, false);
      templateModal.open(responseData.id);
      await revalidateWebsiteModule();
      await revalidateUserDomainsWithWebsite();
    } catch (e) {
      handle422Error(e, setErrors);
      throw e;
    }
  };
  const onEdit = async (instance: WebsiteInterface, data_0: WebsiteInterface) => {
    try {
      setErrors(websiteDefaultErrors);
      const newData: WebsiteUpdateInterface = {
        homePagePath: data_0.homePagePath,
        name: data_0.name,
        domainId: data_0.domainId
      };
      const responseData_0 = await updateWebsite(instance.id, newData);
      await mutate(data_1 => data_1 && {
        ...data_1,
        items: data_1.items.map(item => item.id === instance.id ? responseData_0 : item)
      }, false);
      await revalidateWebsiteModule();
      await revalidateUserDomainsWithWebsite();
    } catch (e_0) {
      handle422Error(e_0, setErrors);
      throw e_0;
    }
  };
  const onChangeStatus = async (website_0: WebsiteInterface) => {
    try {
      setErrors(websiteDefaultErrors);
      await updateStatus(website_0.active, website_0.id);
      await mutate(prev => prev && {
        ...prev,
        items: prev.items.map(item_0 => item_0.id === website_0.id ? {
          ...item_0,
          active: !item_0.active
        } : item_0)
      }, false);
      await revalidateWebsiteModule();
    } catch (e_1) {
      handle422Error(e_1, setErrors);
      throw e_1;
    }
  };
  const onRemoveWebsite = async (website_1: WebsiteInterface) => {
    await removeWebsite(website_1.id);
    await revalidateWebsiteModule();
    await revalidateUserDomainsWithWebsite();
  };
  const userDomainsWithWebsiteSelect = useMemo(() => Array.isArray(userDomainsWithWebsite) ? userDomainsWithWebsite.map(el => ({
    caption: el.name,
    id: el.id,
    additionalRender: <Badge text={el.website ? t('dashboard.website.domain_in_use') : t('dashboard.website.domain_available')} classNames={twMerge('group-data-[focus]:text-background-secondary group-data-[selected]:text-background-secondary group-hover:text-background-secondary', el.website ? 'text-red' : 'text-green')} />
  })) : undefined, [userDomainsWithWebsite]);
  useEffect(() => {
    if (user?.dashboardLocale) {
      setTempState(prev_0 => ({
        ...prev_0,
        locale: user.dashboardLocale
      }));
    }
  }, [user?.dashboardLocale]);
  const onModalClose = () => setErrors(websiteDefaultErrors);
  return <>
      <ListingLayout counter={websites && {
      count: websites.total
    }} data={websites && {
      items: websites.items,
      hasMore: false
    }} title={websites ? tICU('dashboard.website.title_icu', {
      count: Number(websites.total)
    }) : t('dashboard.website.title')} type={'compact'} customHeaderElement={<WebsiteCreateModal disabled={!websites || !userDomainsWithWebsite} defaultValues={tempState} errors={errors} userDomainsWithWebsiteSelect={userDomainsWithWebsiteSelect} isDomainOccupied={id => !!isDomainOccupied(id)} onCreate={data_2 => onCreate({
      ...tempState,
      ...data_2
    })} afterLeave={() => {
      setErrors(websiteDefaultErrors);
    }} />}>
        <Table caption={t('dashboard.website.title')} data={websites && {
        items: websites.items,
        hasMore: false
      }} columns={websiteTableColumns({
        t,
        userDomainsWithWebsite,
        onNameClick: (websiteId: WebsiteId) => {
          templateModal.open(websiteId);
        }
      })} actions={{
        settings: {
          onEdit: onEdit,
          onModalClose: onModalClose,
          toastCaption: t('dashboard.website.edit_toast_successful'),
          editable: [{
            field: 'name',
            onRender: (value, onChange) => <FormInput required key="website-name-settings" className={'w-full'} label={t('global.name')} value={value} onChange={e_2 => onChange(e_2.target.value)} error={errors.name} />
          }, {
            field: 'domainId',
            onRender: (value_0, onChange_0, _, website_2) => {
              const isWebsiteDomainOccupied = value_0 && isDomainOccupied(value_0) && !(website_2.domainId && isDomainOccupied(website_2.domainId));
              return <FormSelectUserDomainWithWebsite key="domainId-active-settings" onChange={value_1 => onChange_0(value_1)} value={value_0} data={userDomainsWithWebsiteSelect} errors={errors} isDomainOccupied={!!isWebsiteDomainOccupied} prevDomainId={website_2.domainId} />;
            }
          }, {
            field: 'homePagePath',
            shouldRender: website_3 => !!website_3.domainId,
            onRender: (value_2, onChange_1, data_3) => <FormUrlPathInput key="home-page-path-settings" error={errors.homePagePath} value={value_2} onChange={e_3 => onChange_1(e_3.target.value)} onFocus={e_4 => {
              if (!e_4.target.value) {
                onChange_1(generatePathFromName(data_3.name));
              }
            }} label={t('dashboard.website.home_page_title')} domainName={userDomainsWithWebsiteSelect?.find(el_0 => el_0.id === data_3.domainId)?.caption} />
          }]
        },
        custom: [{
          name: 'activate-website',
          shouldRender: website_4 => !website_4.active && website_4.domainId !== null && website_4.templateId !== null,
          caption: t('dashboard.actions.title.activate'),
          onActionRender: (opened, onClose, website_5, key) => <ActivateWebsiteModal key={key} opened={opened} onClose={onClose} onChangeStatus={() => onChangeStatus(website_5)} errors={errors} afterLeave={onModalClose} />
        }, {
          name: 'deactivate-website',
          shouldRender: website_6 => website_6.active && website_6.domainId !== null && website_6.templateId !== null,
          caption: t('dashboard.actions.title.deactivate'),
          onActionRender: (opened_0, onClose_0, website_7, key_0) => <Confirmation key={key_0} opened={opened_0} onClose={onClose_0} onConfirm={() => onChangeStatus(website_7)} confirmationText={t('dashboard.actions.activate_confirmation', {
            activate: t('global.deactivate').toLowerCase(),
            module: t('dashboard.website.title').toLowerCase()
          })} toastCaption={t('dashboard.actions.activated', {
            module: t('dashboard.website.title'),
            activated: t('dashboard.actions.deactivated_caption').toLowerCase()
          })} />
        }],
        remove: {
          onChange: website_8 => onRemoveWebsite(website_8)
        },
        openLink: [{
          name: 'view',
          setHref(website_9) {
            return getUrlWebsiteView(website_9, userDomainsWithWebsite);
          },
          openInNew: true,
          shouldRender: website_10 => website_10.templateId !== null
        }]
      }} />
      </ListingLayout>
      {templateModal.data && <WebsiteTemplateModal websiteId={templateModal.data} opened={templateModal.opened} onClose={() => {
      templateModal.close();
    }} afterLeave={() => templateModal.cleanData()} />}
    </>;
};
export const getServerSideProps = getBaseServerSideProps(WebsiteLoco, 'header.top_menu.website');
export default Website;